import React, { useEffect, useState } from "react";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { ApolloError, gql } from "@apollo/client";
import { toast } from "react-toastify";
import { useLazyQuery, useMutation } from "@apollo/client";

import { Button } from "src/components/button/button";
import { Input } from "src/components/input/input";
import { parseError } from "src/utils/parse_error";
import { CloudCodeFunction, CreateUserInput, CreateUserPayload, UserConnection, Viewer } from "src/generated/types";
import { Error } from "src/components/error/error";
import { Loading } from "src/components/loading/loading";

/*
 * GraphQL
 */

const CREATE_USER_MUTATION = gql`
    mutation CreateUser($input: CreateUserInput!) {
        createUser(input: $input) {
            user {
                id
            }
        }
    }
`;

const DELETE_USER_MUTATION = gql`
    mutation UpdateUser($input: CallCloudCodeInput!) {
        callCloudCode(input: $input) {
            result
        }
    }
`;

const ALL_SCHOOL_ADMIN_QUERY = gql`
    query ListSchoolAdmins($schoolId: ID) {
        viewer {
            user {
                id
            }
        }
        users(where: { school: { have: { objectId: { equalTo: $schoolId } } } }) {
            edges {
                node {
                    id
                    school {
                        id
                    }
                    email
                    objectId
                    username
                }
            }
        }
    }
`;

/*
 * Types
 */

interface ManageUserFormProps {
    schoolId?: string;
}

interface ApolloMutationVarsCreate {
    input: CreateUserInput;
}

interface ApolloMutationDataCreate {
    createUser: CreateUserPayload;
}

interface ApolloQueryData {
    readonly users: UserConnection;
    readonly viewer: Viewer;
}

interface ApolloQueryVars {
    readonly schoolId: string;
}

interface SchoolAdmin {
    readonly id: string;
    readonly objectId: string;
    readonly email: string;
    readonly schoolId: string;
}

interface FormValues {
    email: string;
    password: string;
    passwordConfirm: string;
}

/*
 * Main
 */

export const ManageUsersForm = (props: ManageUserFormProps): React.ReactElement => {
    const { schoolId } = props;

    const [showAdmins, setShowAdmins] = useState(false);
    const toggleAdminList = () => setShowAdmins(!showAdmins);

    const defaultSchoolAdminList: SchoolAdmin[] = [];
    const [schoolAdminList, setSchoolAdminList] = useState(defaultSchoolAdminList);
    const [getSchoolAdmins, { data, error, loading }] = useLazyQuery<ApolloQueryData, ApolloQueryVars>(
        ALL_SCHOOL_ADMIN_QUERY,
        {
            fetchPolicy: "network-only",
        },
    );

    const [createUser, { loading: submitting }] = useMutation<ApolloMutationDataCreate, ApolloMutationVarsCreate>(
        CREATE_USER_MUTATION,
        {
            refetchQueries: () => [
                {
                    query: ALL_SCHOOL_ADMIN_QUERY,
                    variables: { schoolId: schoolId ?? "" },
                },
            ],
        },
    );
    const initialValues: FormValues = {
        email: "",
        password: "",
        passwordConfirm: "",
    };
    const [deleteUser] = useMutation(DELETE_USER_MUTATION, {
        refetchQueries: () => [
            {
                query: ALL_SCHOOL_ADMIN_QUERY,
                variables: { schoolId: schoolId ?? "" },
            },
        ],
    });

    // Set school admins data
    useEffect(() => {
        if (data?.users) {
            const schoolAdmins = data.users.edges?.map((edge) => {
                return {
                    id: edge?.node?.id ?? "",
                    email: edge?.node?.username ?? "",
                    objectId: edge?.node?.objectId ?? "",
                    schoolId: edge?.node?.school?.id ?? "",
                };
            });
            if (schoolAdmins) {
                setSchoolAdminList(schoolAdmins);
            }
        }
    }, [data]);

    // Call GET school admins query
    useEffect(() => {
        getSchoolAdmins({
            variables: { schoolId: schoolId ?? "" },
        });
    }, []);

    if (loading) return <Loading />;
    if (error) return <Error />;

    return (
        <>
            <Formik
                initialValues={initialValues}
                onSubmit={(values, { resetForm, setSubmitting }): void => {
                    createUser({
                        variables: {
                            input: {
                                fields: {
                                    isAdmin: false,
                                    email: values.email,
                                    password: values.password,
                                    school: {
                                        link: schoolId,
                                    },
                                    username: values.email,
                                },
                            },
                        },
                    })
                        .then(() => {
                            toast.success("User created successfully");
                        })
                        .catch((e: ApolloError) => {
                            toast.error(parseError(e));
                        });

                    setSubmitting(false);
                    resetForm();
                }}
                validationSchema={Yup.object().shape({
                    email: Yup.string().required("Required"),
                    password: Yup.string().min(2, "Too Short!").max(50, "Too Long!").required("Required"),
                    passwordConfirm: Yup.string()
                        .oneOf([Yup.ref("password"), null], "Passwords must match")
                        .required("Required"),
                })}
            >
                {(formik: FormikProps<FormValues>) => (
                    <Form>
                        <div className="spacer" />
                        <Input
                            autoComplete="off"
                            id="email"
                            label="Email"
                            name="email"
                            placeholder="Enter Email Address"
                            type="email"
                        />
                        <Input
                            autoComplete="new-password"
                            id="password"
                            label="Password"
                            name="password"
                            placeholder="Enter Password"
                            type="password"
                        />
                        <Input
                            autoComplete="new-password"
                            id="passwordConfirm"
                            label="Confirm Password"
                            name="passwordConfirm"
                            placeholder="Confirm Password"
                            type="password"
                        />
                        <Button disabled={formik.isSubmitting || !formik.isValid || submitting} type="submit">
                            Add User
                        </Button>
                    </Form>
                )}
            </Formik>
            <div className="view-school-admin-label" onClick={toggleAdminList}>
                View Additional User Admins
            </div>
            <div>
                {showAdmins &&
                    schoolAdminList
                        .filter(
                            (e) =>
                                e.id === data?.viewer?.user.id ||
                                (!e.email.includes("@fsci.org") && !e.email.includes("@acsi.org")),
                        )
                        .map((user) => {
                            const isCurrentUser = user.id === data?.viewer?.user.id;
                            return (
                                <div className="school-admin-list-wrapper" key={user.id}>
                                    <div className="school-admin-list-item">{user.email}</div>
                                    <Button
                                        className={
                                            isCurrentUser
                                                ? "school-admin-list-button disabled"
                                                : "school-admin-list-button"
                                        }
                                        disabled={isCurrentUser}
                                        onClick={(): void => {
                                            deleteUser({
                                                variables: {
                                                    input: {
                                                        functionName: CloudCodeFunction.DeleteUser,
                                                        params: {
                                                            userId: user.objectId,
                                                        },
                                                    },
                                                },
                                            })
                                                .then(() => {
                                                    toast.success("User deleted successfully");
                                                })
                                                .catch((e: ApolloError) => {
                                                    toast.error(parseError(e));
                                                });
                                        }}
                                    >
                                        Delete User
                                    </Button>
                                </div>
                            );
                        })}
            </div>
        </>
    );
};
